import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "./App.css";
import Login from "./components/Login";
import Upload from "./components/Upload";

function App() {
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCards, setExpandedCards] = useState({});
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const toggleExpand = (uploadDate) => {
    setExpandedCards((prev) => ({
      ...prev,
      [uploadDate]: !prev[uploadDate],
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem("adminCredentials");
    setCredentials({
      username: "",
      password: "",
    });
    setIsAdmin(false);
  };

  useEffect(() => {
    fetchImages();
    const savedCredentials = localStorage.getItem("adminCredentials");
    if (savedCredentials) {
      const parsed = JSON.parse(savedCredentials);
      setCredentials(parsed);
      setIsAdmin(true);
    }
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/images`
      );
      const data = await response.json();
      setImages(data);
    } catch (err) {
      setError("Failed to fetch images");
    }
  };

  const handleDelete = async (uploadDate) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/images/${uploadDate}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");
      fetchImages();
    } catch (err) {
      setError("Failed to delete image");
    }
  };

  const filteredImages = images.filter((image) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      image.name?.toLowerCase().includes(searchLower) ||
      image.code?.toLowerCase().includes(searchLower) ||
      image.tags?.toLowerCase().includes(searchLower) ||
      image.map_size?.toLowerCase().includes(searchLower) ||
      image.player_config?.toLowerCase().includes(searchLower) ||
      image.team_positions?.toLowerCase().includes(searchLower) ||
      image.biome?.toLowerCase().includes(searchLower) ||
      image.map_type?.toLowerCase().includes(searchLower) ||
      image.uploader?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Router>
      <div className="app-container">
        <nav className="nav-header">
          <Link to="/" className="nav-logo" onClick={() => setSearchQuery("")}>
            <h1>AOE Maps</h1>
          </Link>
          <div className="nav-buttons">
            {isAdmin ? (
              <>
                <Link to="/upload" className="nav-button">
                  Upload
                </Link>
                <button onClick={handleLogout} className="nav-button">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/upload" className="nav-button">
                  Upload
                </Link>
              </>
            )}
          </div>
        </nav>

        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/upload"
            element={
              <Upload credentials={credentials} onUploadSuccess={fetchImages} />
            }
          />
          <Route
            path="/"
            element={
              <div className="container">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search by map name, code, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="image-grid">
                  {filteredImages.map((image) => (
                    <div key={image.uploadDate} className="image-card">
                      <img src={image.url} alt={image.name || image.filename} />
                      <div className="image-info">
                        <h3>{image.name || image.filename}</h3>
                        {image.code && (
                          <div className="map-code-container">
                            <p>Code: {image.code}</p>
                            <button
                              className="copy-button"
                              onClick={() => {
                                try {
                                  navigator.clipboard.writeText(image.code);
                                  const toast = document.createElement("div");
                                  toast.className = "toast-notification";
                                  toast.textContent = "Map code copied!";
                                  document.body.appendChild(toast);

                                  // Trigger reflow to ensure the transition works
                                  toast.offsetHeight;
                                  toast.classList.add("show");

                                  setTimeout(() => {
                                    toast.classList.remove("show");
                                    setTimeout(() => {
                                      document.body.removeChild(toast);
                                    }, 300);
                                  }, 2000);
                                } catch (err) {
                                  console.error("Failed to copy:", err);
                                }
                              }}
                              title="Copy map code"
                            >
                              <span className="copy-icon">📋</span>
                              <span className="copy-text">Copy Code</span>
                            </button>
                          </div>
                        )}
                        <div className="map-details-container">
                          <div className="map-details-basic">
                            {image.tags && (
                              <div className="tags-container">
                                <p>Tags: {image.tags}</p>
                              </div>
                            )}
                          </div>
                          <div
                            className="map-details-wrapper"
                            onClick={() => toggleExpand(image.uploadDate)}
                          >
                            <div
                              className={`map-details-expanded ${
                                expandedCards[image.uploadDate] ? "active" : ""
                              }`}
                            >
                              <div className="map-details-grid">
                                {image.map_size && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">Size:</span>
                                    <span className="detail-value">
                                      {image.map_size}
                                    </span>
                                  </div>
                                )}
                                {image.player_config && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">
                                      Players:
                                    </span>
                                    <span className="detail-value">
                                      {image.player_config}
                                    </span>
                                  </div>
                                )}
                                {image.team_positions && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">
                                      Team Start:
                                    </span>
                                    <span className="detail-value">
                                      {image.team_positions}
                                    </span>
                                  </div>
                                )}
                                {image.biome && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">Biome:</span>
                                    <span className="detail-value">
                                      {image.biome}
                                    </span>
                                  </div>
                                )}
                                {image.map_type && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">Type:</span>
                                    <span className="detail-value">
                                      {image.map_type}
                                    </span>
                                  </div>
                                )}
                                {(image.uploader ||
                                  (isAdmin && image.uploader_username)) && (
                                  <div className="map-detail-item">
                                    <span className="detail-label">
                                      Uploaded by:
                                    </span>
                                    <span className="detail-value">
                                      {isAdmin
                                        ? image.uploader_username
                                        : image.uploader}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <button
                              className={`view-more-button ${
                                expandedCards[image.uploadDate] ? "active" : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(image.uploadDate);
                              }}
                            >
                              {expandedCards[image.uploadDate]
                                ? "View Less"
                                : "View More"}
                              <i
                                className={`expand-icon ${
                                  expandedCards[image.uploadDate]
                                    ? "active"
                                    : ""
                                }`}
                              >
                                ▼
                              </i>
                            </button>
                          </div>
                        </div>
                        {isAdmin && credentials.username === "admin" && (
                          <button
                            onClick={() => handleDelete(image.uploadDate)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {error && <p className="error">{error}</p>}
              </div>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
